import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fileUrl, formatPrice, localized } from "@/lib/api";
import { Star } from "lucide-react";
import { HarvestFreshness } from "@/components/HarvestFreshness";

export const ProductCard = ({ product, testid = "product-card" }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const img = product.images?.[0];
  const title = localized(product, "title", lang);
  const region = localized(product, "region", lang);
  const isHr = lang.startsWith("hr");
  const href = isHr && product.slug_hr
    ? `/proizvod/${product.slug_hr}`
    : product.slug
      ? `/p/${product.slug}`
      : `/product/${product.product_id}`;
  return (
    <Link
      to={href}
      className="group block bg-white hairline rounded-sm overflow-hidden transition-all hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(44,42,41,0.08)]"
      data-testid={`${testid}-${product.product_id}`}
    >
      <div className="aspect-[4/5] overflow-hidden bg-boneAlt">
        {img ? (
          <img
            src={fileUrl(img)}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-ink-muted text-xs">No image</div>
        )}
      </div>
      <div className="p-5">
        <p className="overline" data-testid={`product-region-${product.product_id}`}>{region}</p>
        <h3 className="font-serif text-xl mt-2 leading-tight" data-testid={`product-title-${product.product_id}`}>
          {title}
        </h3>
        <p className="text-sm text-ink-soft mt-1">{localized(product, "vendor_name", lang)}</p>
        <div className="flex items-center justify-between mt-4">
          <span className="font-medium" data-testid={`product-price-${product.product_id}`}>
            {formatPrice(product.price, lang)}
            <span className="text-xs text-ink-muted ml-1">/ {product.volume_ml}ml</span>
          </span>
          {product.review_count > 0 && (
            <span className="text-xs text-ink-soft flex items-center gap-1">
              <Star className="w-3 h-3 fill-current text-terracotta" />
              {product.rating.toFixed(1)} <span className="text-ink-muted">({product.review_count})</span>
            </span>
          )}
        </div>
        {product.organic && (
          <span className="inline-block mt-3 text-[10px] uppercase tracking-widest text-olive border border-olive/30 px-2 py-0.5">
            Organic
          </span>
        )}
        <HarvestFreshness hoursToPress={product.hours_to_press} harvestDate={product.harvest_date} compact={true} />
      </div>
    </Link>
  );
};
